import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { firstValueFrom, Subject } from 'rxjs';
import type { WalletEntryKind, WalletLimitWindowDays, WorkspaceOfferingAccessMode } from 'voxr-shared';
import { environment } from '../../environments/environment';
import { RECORD_HTTP_ERROR_WITHOUT_TOAST } from '../interceptors/error.interceptor';
import { SocketService } from './socket.service';
import { WorkspaceService } from './workspace.service';

export type WalletSpendingMode = 'budget' | 'outside';
export type WalletOfferingUsage = { key: string; name: string; spendingMode: WalletSpendingMode; limitCents: number | null; usedCents: number };
export type WalletLimitsView = {
  windowDays: WalletLimitWindowDays;
  windowStart: string;
  usage: { limitCents: number | null; usedCents: number };
  offerings: WalletOfferingUsage[];
};
export type WalletView = WalletLimitsView & {
  balanceCents: number;
  negativeCause?: { entryId: string; offeringName?: string; amountCents: number; createdAt: string };
};
export type WalletEntryItem = {
  id: string;
  kind: WalletEntryKind;
  amountCents: number;
  balanceAfterCents: number;
  offeringKey?: string;
  offeringName?: string;
  adminRefundAllowed?: boolean;
  reason?: string;
  note?: string;
  actor: { type: 'user' | 'api' | 'system'; displayName: string; accessType?: string; selfAdjust?: boolean };
  reversibleByViewer?: boolean;
  refundedByEntryId?: string;
  reversedByEntryId?: string;
  createdAt: string;
};
export type WalletEntryPage = { items: WalletEntryItem[]; nextCursor: string | null };
export type WalletMemberBalances = {
  current: { userId: string; balanceCents: number }[];
  former: { userId: string; email?: string; balanceCents: number; state: 'former' | 'coach-only' }[];
};
export type WalletWriteResult = {
  entry: Pick<WalletEntryItem, 'id' | 'kind' | 'amountCents' | 'balanceAfterCents' | 'offeringKey' | 'reason' | 'note' | 'createdAt'>;
  balanceCents: number;
};
export type WalletAdjustInput = { direction: 'add' | 'remove'; amountCents: number; reason: string; note?: string; clientToken: string };
export type WalletLimitsInput = {
  windowDays: WalletLimitWindowDays;
  usage: { amountCents: number | null };
  perOffering: Record<string, { amountCents: number | null }>;
};
export type WorkspaceOfferingItem = {
  key: string; name: string; description?: string; spendingMode: WalletSpendingMode; adminRefunds: boolean;
  enabled: boolean; accessMode: WorkspaceOfferingAccessMode; agentIds: string[];
};
export type WorkspaceOfferingInput = Pick<WorkspaceOfferingItem, 'enabled' | 'accessMode' | 'agentIds'>;
export type WalletBalanceChangedEvent = { workspaceId: string; userId?: string; balanceCents: number; entryId?: string };

/** The `{ code, message, field?, existing? }` body of every wallet route error (PLAN 5.1). */
export type WalletErrorBody = {
  status: number;
  code?: string;
  message?: string;
  field?: string;
  existing?: { actor?: { type?: string }; reason?: string; createdAt?: string };
};

export function walletErrorOf(error: unknown): WalletErrorBody {
  const status = error instanceof HttpErrorResponse ? error.status : (error as { status?: number })?.status ?? 0;
  const body = (error as { error?: Partial<WalletErrorBody> })?.error;
  return { status, ...(body && typeof body === 'object' ? body : {}) };
}

/** Roles exactly `coach`: such a member holds no spending wallet (the server's eligibility rule). */
export function isCoachOnlyRoles(roles: readonly string[] | undefined) {
  const unique = new Set(roles || []);
  return unique.size === 1 && unique.has('coach');
}

/** Generated once per write draft and reused for every retry of it (PLAN 5.1). */
export function newClientToken(): string {
  return crypto.randomUUID().replace(/-/g, '');
}

@Injectable({ providedIn: 'root' })
export class WalletService {
  private http = inject(HttpClient);
  private workspaceService = inject(WorkspaceService);
  private socketService = inject(SocketService);
  private readonly url = `${environment.app.serviceUrl}/workspace`;
  // Every caller shows its own inline message or toast, so the global interceptor only records the error.
  private readonly silent = { context: new HttpContext().set(RECORD_HTTP_ERROR_WITHOUT_TOAST, true) };

  /** The current user's balance in `balanceWorkspaceId`; `null` while unknown. */
  readonly balance = signal<number | null>(null);
  balanceWorkspaceId: string | null = null;
  private readonly changedSubject = new Subject<WalletBalanceChangedEvent>();
  /** Emits after a balance event for the workspace whose balance is shown. */
  readonly changed$ = this.changedSubject.asObservable();

  constructor() {
    this.socketService.onEvent$<WalletBalanceChangedEvent>('wallet-balance-changed').subscribe(event => {
      if (!event || !this.balanceWorkspaceId || event.workspaceId !== this.balanceWorkspaceId) return;
      this.balance.set(event.balanceCents);
      this.changedSubject.next(event);
    });
  }

  reset() {
    this.balance.set(null);
    this.balanceWorkspaceId = null;
  }

  /**
   * Reads the member's own wallet and records its balance. Resolves `null`, recording nothing, when the workspace
   * changed while the read was in flight, so a late response never renders in the newly selected workspace.
   * A failure clears the balance, so no chip shows a stale value, and rethrows for the caller's own message.
   */
  async getMyWallet(): Promise<WalletView | null> {
    const workspaceId = this.workspaceService.workspaceId ?? null;
    const current = () => workspaceId === (this.workspaceService.workspaceId ?? null);
    let wallet: WalletView;
    try {
      wallet = await firstValueFrom(this.http.get<WalletView>(`${this.url}/wallet/me`, this.silent));
    } catch (error) {
      if (current()) this.balance.set(null);
      throw error;
    }
    if (!current()) return null;
    this.balanceWorkspaceId = workspaceId;
    this.balance.set(wallet.balanceCents);
    return wallet;
  }

  /** Never throws: resolves `null` on a failure or a stale read. */
  async loadMyWallet(): Promise<WalletView | null> {
    try {
      return await this.getMyWallet();
    } catch {
      return null;
    }
  }

  listMyEntries(cursor?: string) {
    return firstValueFrom(this.http.get<WalletEntryPage>(`${this.url}/wallet/me/entries`, { ...this.silent, params: this.pageParams(cursor) }));
  }

  listMemberBalances() {
    return firstValueFrom(this.http.get<WalletMemberBalances>(`${this.url}/wallet/members`, this.silent));
  }

  listMemberEntries(userId: string, cursor?: string) {
    return firstValueFrom(this.http.get<WalletEntryPage>(`${this.url}/wallet/members/${userId}/entries`, { ...this.silent, params: this.pageParams(cursor) }));
  }

  /** `ownWallet`: the member is the signed-in user, so the shown balance follows the write. */
  adjust(userId: string, input: WalletAdjustInput, ownWallet = false) {
    return this.write(this.http.post<WalletWriteResult>(`${this.url}/wallet/members/${userId}/adjust`, input, this.silent), ownWallet);
  }

  refund(entryId: string, input: { reason: string; clientToken: string }, ownWallet = false) {
    return this.write(this.http.post<WalletWriteResult>(`${this.url}/wallet/entries/${entryId}/refund`, input, this.silent), ownWallet);
  }

  reverse(entryId: string, input: { reason?: string; note?: string; clientToken: string }, ownWallet = false) {
    return this.write(this.http.post<WalletWriteResult>(`${this.url}/wallet/entries/${entryId}/reverse`, input, this.silent), ownWallet);
  }

  getLimits(userId: string) {
    return firstValueFrom(this.http.get<WalletLimitsView>(`${this.url}/wallet/members/${userId}/limits`, this.silent));
  }

  saveLimits(userId: string, input: WalletLimitsInput) {
    return firstValueFrom(this.http.put<WalletLimitsView>(`${this.url}/wallet/members/${userId}/limits`, input, this.silent));
  }

  listOfferings() {
    return firstValueFrom(this.http.get<{ items: WorkspaceOfferingItem[] }>(`${this.url}/offerings`, this.silent));
  }

  saveOffering(key: string, input: WorkspaceOfferingInput) {
    return firstValueFrom(this.http.put<{ item: WorkspaceOfferingItem }>(`${this.url}/offerings/${encodeURIComponent(key)}`, input, this.silent));
  }

  private async write(request: ReturnType<HttpClient['post']>, ownWallet: boolean): Promise<WalletWriteResult> {
    const workspaceId = this.workspaceService.workspaceId ?? null;
    const result = await firstValueFrom(request) as WalletWriteResult;
    if (ownWallet && workspaceId && workspaceId === this.balanceWorkspaceId && workspaceId === (this.workspaceService.workspaceId ?? null))
      this.balance.set(result.balanceCents);
    return result;
  }

  private pageParams(cursor: string | undefined): Record<string, string> {
    return { limit: '25', ...(cursor ? { cursor } : {}) };
  }
}
